<script setup lang="ts">
import type { PrimitiveProps } from 'reka-ui';
import type { HTMLAttributes } from 'vue';
import type { LinkVariants } from './variants';
import { ExternalLink } from 'lucide';
import { Primitive } from 'reka-ui';
import { computed, useSlots, watchEffect } from 'vue';
import { SphIcon } from '@/components/base/icon/';
import { useI18n } from '@/composables/i18n';
import { cn } from '@/utils/shadcn/lib/utils';
import { linkVariants } from './variants';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = withDefaults(defineProps<Props>(), {
    as: 'a',
    href: undefined,
    newTab: undefined,
    rel: undefined,
    variant: 'default',
    label: '',
    class: '',
});

const SAFE_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];
const NAVIGATING_PROTOCOLS = ['http:', 'https:'];
const LEADING_WWW = /^www\./;
const TRAILING_SLASH = /\/$/;
const WHITESPACE = /\s+/;

// No `asChild`: the text, icon and hint spans would be the slot's children, so Reka would merge
// href/target/rel into the text span instead of the consumer's element.
interface Props extends Omit<PrimitiveProps, 'asChild'> {
    href?: string
    newTab?: boolean
    rel?: string
    variant?: LinkVariants['variant']
    label?: string
    class?: HTMLAttributes['class']
}

const { i18n } = useI18n();
const slots = useSlots();

// `as="button"` is a link-styled action: href, target and rel would be meaningless there.
const isAnchor = computed<boolean>(() => props.as === 'a');

const parsedHref = computed<URL | null>(() => {
    const raw = props.href?.trim();
    if (!raw || !isAnchor.value) {
        return null;
    }

    try {
        return new URL(raw, window.location.href);
    } catch {
        return null;
    }
});

const isSafe = computed<boolean>(
    () => !!parsedHref.value && SAFE_PROTOCOLS.includes(parsedHref.value.protocol),
);

// Dropped rather than rewritten to '#': inert text is honest, a dead link is not.
const resolvedHref = computed<string | undefined>(() => (isSafe.value ? props.href : undefined));

const isExternal = computed<boolean>(() => {
    const url = parsedHref.value;
    if (!url || !isSafe.value || !NAVIGATING_PROTOCOLS.includes(url.protocol)) {
        return false;
    }

    return url.origin !== window.location.origin;
});

const opensInNewTab = computed<boolean>(() => {
    const url = parsedHref.value;
    if (!url || !isSafe.value || !NAVIGATING_PROTOCOLS.includes(url.protocol)) {
        return false;
    }

    return isExternal.value || (props.newTab ?? false);
});

// A custom `rel` replaces `noreferrer` (for links that need the referrer), never `noopener`.
const resolvedRel = computed<string | undefined>(() => {
    const tokens = props.rel?.split(WHITESPACE).filter(Boolean) ?? [];

    if (opensInNewTab.value) {
        tokens.push('noopener');
        if (props.rel === undefined && isExternal.value) {
            tokens.push('noreferrer');
        }
    }

    const unique = [...new Set(tokens)];

    return unique.length > 0 ? unique.join(' ') : undefined;
});

function decoded(value: string): string {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
}

const formattedHref = computed<string>(() => {
    const url = parsedHref.value;
    // Includes the dropped-protocol case: the href is inert, but the string the data holds is
    // still more honest to show than a blank link.
    if (!url || !isSafe.value) {
        return props.href?.trim() ?? '';
    }
    if (!NAVIGATING_PROTOCOLS.includes(url.protocol)) {
        return decoded(url.pathname);
    }

    const host = url.host.replace(LEADING_WWW, '');
    const path = decoded(`${url.pathname}${url.search}${url.hash}`).replace(TRAILING_SLASH, '');

    return (isExternal.value ? `${host}${path}` : path) || host;
});

const displayLabel = computed<string>(() => props.label || formattedHref.value);

// U+2060 word joiner, appended to the text: removes the line-break opportunity before the icon,
// so a wrapping link takes the icon along with its last word instead of orphaning it on its own
// line. It lives in the text node rather than the template because any whitespace the template
// compiler leaves between the two would put the break opportunity right back.
const wordJoiner = computed<string>(() => (opensInNewTab.value ? '\u2060' : ''));

watchEffect(() => {
    if (props.href && isAnchor.value && !isSafe.value) {
        console.error(`[SphLink] href dropped, protocol not allowed: ${props.href}`);
    }
});

// Usage mistakes by contrast: nothing an end user can trigger, so they stay out of production.
if (import.meta.env.DEV) {
    watchEffect(() => {
        if (props.newTab === false && isExternal.value) {
            console.warn(`[SphLink] newTab: false ignored, external links always open in a new tab: ${props.href}`);
        }
        if (!slots.default && !displayLabel.value) {
            console.warn('[SphLink] renders empty: pass a label, slot content, or an href to fall back to.');
        }
    });
}
</script>

<template>
    <Primitive
        data-slot="link"
        :as="props.as"
        :type="props.as === 'button' ? 'button' : undefined"
        :class="cn(linkVariants({ variant: props.variant }), props.class)"
        :href="resolvedHref"
        :target="opensInNewTab ? '_blank' : undefined"
        :rel="resolvedRel"
    >
        <span class="sph-link__text"><slot>{{ displayLabel }}</slot>{{ wordJoiner }}</span><SphIcon
            v-if="opensInNewTab"
            :icon="ExternalLink"
            size="inherit"
            class="sph-link__icon"
        /><span v-if="opensInNewTab" class="sr-only">{{ i18n.t('(öffnet in neuem Tab)') }}</span>
    </Primitive>
</template>

<style>
@reference '@/assets/styles/index.css';

.sph-link {
    display: inline;
    text-decoration: none;

    --sph-link-underline-width: 2px;
    --sph-link-underline-offset: 2px;
    --sph-link-underline-rest: 40%;
    --sph-link-underline-active: 70%;
    --sph-link-duration: 200ms;

    transition: color var(--sph-link-duration) var(--ease-out-quad);
}

/*
 * A real text-decoration rather than a drawn background: only the browser's skip-ink can cut the
 * underline around descenders without knowing the surface colour behind the link. The gap width
 * is the browser's to choose.
 */
.sph-link__text {
    text-decoration-line: underline;
    text-decoration-thickness: var(--sph-link-underline-width);
    text-decoration-color: color-mix(in srgb, currentColor var(--sph-link-underline-rest), transparent);
    text-decoration-skip-ink: all;
    text-underline-offset: var(--sph-link-underline-offset);
    transition: text-decoration-color var(--sph-link-duration) var(--ease-out-quad);
}

.sph-link__icon {
    vertical-align: -0.1em;
    margin-inline-start: 0.25em;
    transition: transform var(--sph-link-duration) var(--ease-out-quad);
}

.sph-link:hover {
    --sph-link-duration: 260ms;

    .sph-link__text {
        text-decoration-color: color-mix(in srgb, currentColor var(--sph-link-underline-active), transparent);
    }

    .sph-link__icon {
        transition-delay: 70ms;
        transform: translate(0.1em, -0.05em);
    }
}

@media (prefers-reduced-motion: reduce) {
    .sph-link__text,
    .sph-link__icon {
        transition: none;
    }
}
</style>
